import {useState, useEffect, useCallback} from 'react';
import chatApi from '../Services/chatApi';
import { useUser } from '../contexts/UserContext';

export const useChat = () => {
    const { user } = useUser(); // Obtener el usuario del contexto
    const [conversations, setConversations] = useState([]);
    const [activeConversation, setActiveConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [error, setError] = useState(null);
    const [plantillas, setPlantillas] = useState([]);
    
    const loadConversations = useCallback(async (filters = {})=>{
        try {
            setLoading(true);
            setError(null);
            const data = await chatApi.getConversations(filters);
            setConversations(data);
        }catch (error) {
            console.error('Error loading conversations: ', error);
            setError(error.message);
        } finally{
            setLoading(false);
        }
    },[] );

    /** Devuelve true si la URL es un blob SAS de Azure y el token ya expiró */
    const sasSasExpirado = (url) => {
        if (!url || typeof url !== 'string') return false;
        try {
            const u = new URL(url);
            const se = u.searchParams.get('se');
            if (!se) return false;
            return new Date(se) < new Date();
        } catch {
            return false;
        }
    };

    const selectConversation = useCallback(async (conversationId) =>{
        try{
            setLoading(true);

            await chatApi.markAsRead(conversationId);
            let messageData = await chatApi.getMessages(conversationId);
            const conversationData = await chatApi.getConversationById(conversationId);
            
            const possibleClientsResponse = await chatApi.getPossibleClients(conversationId);

            const possibleClients = possibleClientsResponse?.$values || [];
            setActiveConversation({...conversationData, possibleClients});
            let mensajes = messageData.mensajes || [];

            // Si hay mensajes con SAS expirado, renovar en bloque y recargar
            const tieneExpirados = mensajes.some(m => sasSasExpirado(m.urlMedia));
            if (tieneExpirados) {
                try {
                    await chatApi.renovarUrlsConversacion(conversationId);
                    const refreshed = await chatApi.getMessages(conversationId);
                    mensajes = refreshed.mensajes || [];
                } catch (e) {
                    console.warn('No se pudieron renovar las URLs expiradas:', e);
                }
            }

            setMessages(mensajes);

            setConversations(prev => prev.map(conv => 
                conv.id === conversationId ? {...conv, noLeidos: 0} : conv
                ));
            
        }catch(error){
            console.error('Error selecting conversation: ', error);
            setMessages([]); // Asegurar que messages sea siempre un array
        }finally{
            setLoading(false);
        }
    },[]);

    const sendMessage = useCallback(async (content, username, type = 'text') =>{
        if(!activeConversation) return;
        try{
            const newMessage = await chatApi.sendMessage(activeConversation.id, content, username.nombreCompleto, type);
            setMessages(prev => [...prev, newMessage]);

            setConversations(prev => 
                prev.map(conv =>
                    conv.id === activeConversation.id
                        ? {...conv, ultimoMensaje:content, fechaUltimoMensaje: newMessage.fechaMensaje, enviadoPor: username.nombreCompleto}
                        : conv
                )
            );
            
        }catch (error){
            console.error('Error sending message: ', error);
            throw error;
        }
    },[activeConversation]);

        const searchConversations = useCallback(async (termino = '') => {
        try {
            setLoading(true);
            setError(null);

            const data = await chatApi.searchConversations(termino);
             const conversacionesArray = data?.$values || [];
            setConversations(conversacionesArray);

        } catch (error) {
            console.error('Error searching conversations:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const sendFile = useCallback(async (file, caption = '') =>{
        if(!activeConversation) return;
        try{
            const newMessage = await chatApi.sendMultimedia(activeConversation.id, file, caption);
            setMessages(prev => [...prev, newMessage]);

            const previewMsg = newMessage.contenido || file.name;
            setConversations(prev => 
                prev.map(conv =>
                    conv.id === activeConversation.id
                        ? {...conv, ultimoMensaje: previewMsg, fechaUltimoMensaje: newMessage.fechaMensaje}
                        : conv
                )
            );
            return newMessage;
        }catch (error){
            console.error('Error sending file: ', error);
            throw error;
        }
    },[activeConversation]);

    const createConversation = useCallback(async (phoneNumber, contactName)=>
       {
        try{
            const newConversation = await chatApi.createConversation({numeroWhatsapp:phoneNumber, nombreContacto: contactName});
            setConversations(prev => [newConversation, ...prev]);
            await selectConversation(newConversation.id);

            return newConversation;
            
        }catch(error){
            console.error('Error creating conversation: ', error);
            throw error;
        }
       },[selectConversation]);

    // const attendConversation = useCallback(async (username)=>{
    //     if(!activeConversation) return;

    //     try{
    //         // Determinar el usuario que atiende
    //         let userToAttend;
    //         if (username && typeof username === 'string') {
    //             userToAttend = username;
    //         } else if (user?.nombreCompleto && typeof user.nombreCompleto === 'string') {
    //             userToAttend = user.nombreCompleto;
    //         } else if (user?.codigoUsuario && typeof user.codigoUsuario === 'string') {
    //             userToAttend = user.codigoUsuario;
    //         } else {
    //             userToAttend = 'admin';
    //         }
            
    //         await chatApi.attendConversation(activeConversation.id, userToAttend);
            
    //         // Actualizar estados
    //         setActiveConversation(prev => ({...prev, atendidaPor: userToAttend}));
    //         setConversations(prev => 
    //             prev.map(conv => 
    //                 conv.id === activeConversation.id 
    //                     ? {...conv, atendidaPor: userToAttend}
    //                     : conv
    //             )
    //         );
    //     }catch (error){
    //         console.error('Error attending conversation: ', error);
    //     }
    // },[activeConversation, user]);

    
   const attendConversation = useCallback(async (conversationParam, username) => {
    // Usar la conversación pasada o fallback a activeConversation
    const conv = conversationParam || activeConversation;
    if (!conv) return;

    try {
        // Determinar el usuario que atiende
        let userToAttend;
        if (username && typeof username === 'string') {
            userToAttend = username;
        } else if (user?.nombreCompleto && typeof user.nombreCompleto === 'string') {
            userToAttend = user.nombreCompleto;
        } else if (user?.codigoUsuario && typeof user.codigoUsuario === 'string') {
            userToAttend = user.codigoUsuario;
        } else {
            userToAttend = 'admin';
        }

        await chatApi.attendConversation(conv.id, userToAttend);

        // Actualizar estados
        setActiveConversation(prev => ({ ...prev, atendidaPor: userToAttend }));
        setConversations(prev =>
            prev.map(c =>
                c.id === conv.id ? { ...c, atendidaPor: userToAttend } : c
            )
        );
    } catch (error) {
        console.error('Error attending conversation: ', error);
    }
}, [activeConversation, user]);


    const closeConversation = useCallback(async ()=>{
        if(!activeConversation) return;

        try{
            await chatApi.closeConversation(activeConversation.id);
            setConversations(prev => prev.filter(conv => conv.id !== activeConversation.id));
            setActiveConversation(null);
            setMessages([]);
        }catch (error){
            console.error('Error closing conversation: ', error)
        }
    },
    [activeConversation]);

    const loadUnreadCount = useCallback(async ()=>{
        try{
            const data = await chatApi.getUnreadCount();
            setUnreadCount(data.totalMensajesNoLeidos);

        }catch(error){
            console.error('Error loading unread count: ', error);
        }
    },[]);

    const refreshMessages = useCallback(async () => {
        if (!activeConversation) return;
        try {
            const messageData = await chatApi.getMessages(activeConversation.id);
            const mensajes = messageData.mensajes || [];
            setMessages(mensajes);
        } catch (error) {
            console.error('Error refreshing messages:', error);
        }
    }, [activeConversation]);

    const possibleClients = useCallback(async (conversationId) => {
        try {
            setLoading(true);

            const clientes = await chatApi.getPossibleClients(conversationId);

            return clientes;
        } catch (error) {
            console.error('Error al obtener clientes posibles:', error);
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    const assignClientToConversation = useCallback(async (conversationId, clientId) => {
        try {
            setLoading(true);

            // Llamar al endpoint PUT definido en chatApi
            const updatedData = await chatApi.updateConversationClient(conversationId, {
                idCliente: clientId
            });

            // Actualizar estado de activeConversation con los nuevos datos
            setActiveConversation(prev => ({
                ...prev,
                idCliente: updatedData.idCliente,
                nombreContacto: updatedData.nombreContacto
            }));

            return updatedData;

        } catch (error) {
            console.error("Error assigning client to conversation:", error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, []);

    const loadPlantillas = useCallback(async () => {
        try {
            const data = await chatApi.getPlantillas();
            setPlantillas(data);
        } catch (error) {
            console.error('Error cargando plantillas:', error);
        }
    }, []);

    const sendTemplate = useCallback(async (nombrePlantilla, variables) => {
        if (!activeConversation?.id) throw new Error('No hay conversación activa');
        const resultado = await chatApi.enviarPlantilla(activeConversation.id, nombrePlantilla, variables);
        if (resultado.estadoEnvio === 'failed') {
            throw new Error('El mensaje no pudo ser entregado. El número podría no tener WhatsApp activo.');
        }
        // Agregar el mensaje al estado local
        const nuevoMensaje = {
            id: resultado.id,
            contenido: resultado.contenido,
            tipoMensaje: resultado.tipoMensaje || 'text',
            direccion: 'saliente',
            fechaMensaje: resultado.fechaMensaje || new Date().toISOString(),
            estadoEnvio: resultado.estadoEnvio,
            leido: false
        };
        setMessages(prev => [...prev, nuevoMensaje]);
        return resultado;
    }, [activeConversation]);

    const searchClientsByPhone = useCallback(async (phoneNumber) => {
        try {
            setLoading(true);

            const clientes = await chatApi.getClientsByPhone(phoneNumber);

            return clientes.$values; // devuelve array [{idCliente, nombreCompleto}]
        } catch (error) {
            console.error('Error al buscar clientes por teléfono:', error);
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        conversations,
        activeConversation,
        messages,
        loading,
        unreadCount,
        error,
        loadConversations,
        selectConversation,
        sendMessage,
        searchConversations, // busqueda
        sendFile,
        createConversation,
        attendConversation,
        closeConversation,
        loadUnreadCount,
        refreshMessages,
        possibleClients,
        assignClientToConversation,
        searchClientsByPhone,
        plantillas,
        loadPlantillas,
        sendTemplate
    };
};